// 📄 components/integracion/EnvironmentTabs.tsx
// Selector DEV/QAS/PRD con badges de estado para la vista de un canal de
// integración. El significado del badge de QAS/PRD depende de si el paso
// requiere OT (`stepRequiresOT`):
//  - Sí requiere OT: DEV es la fuente de verdad y el badge refleja la
//    confirmación de transporte (ver TransportConfirmationPanel).
//  - No requiere OT: cada ambiente mantiene su propia copia editable de
//    los parámetros, y el badge refleja si ya se guardó manualmente ahí
//    (ver el bloque "Guardar Ajustes en QAS/PRD" en cada vista de canal).
// En ambos casos `qas`/`prd` llegan con la misma forma (TransportConfirmation)
// para no duplicar el tipo — solo cambia qué representan sus campos.
import { TransportConfirmation } from '../../types/transportConfirmation.ts';

interface Props {
  selectedEnv: 'DEV' | 'QAS' | 'PRD';
  onChange: (env: 'DEV' | 'QAS' | 'PRD') => void;
  devConfigured: boolean;
  stepRequiresOT: boolean;
  qas: TransportConfirmation;
  prd: TransportConfirmation;
}

const ACTIVE_CLASS: Record<'DEV' | 'QAS' | 'PRD', string> = {
  DEV: 'bg-blue-500 text-slate-950 shadow-md',
  QAS: 'bg-orange-500 text-[#fff] shadow-md',
  PRD: 'bg-red-500 text-[#fff] shadow-md'
};

export default function EnvironmentTabs({ selectedEnv, onChange, devConfigured, stepRequiresOT, qas, prd }: Props) {
  const badgeFor = (env: 'DEV' | 'QAS' | 'PRD'): { color: 'green' | 'orange' | 'none'; title: string } => {
    if (env === 'DEV') {
      return devConfigured
        ? { color: 'green', title: 'Configurado en DEV' }
        : { color: 'none', title: 'Sin configurar en DEV' };
    }
    const status = env === 'QAS' ? qas : prd;
    if (stepRequiresOT) {
      return status.transported
        ? { color: 'green', title: `Transportado a ${env}${status.transportDate ? ` el ${status.transportDate}` : ''}` }
        : { color: 'orange', title: `Pendiente de transporte a ${env}` };
    }
    return status.transported
      ? { color: 'green', title: `Configurado manualmente en ${env}${status.transportDate ? ` el ${status.transportDate}` : ''}` }
      : { color: 'orange', title: `Configuración manual pendiente en ${env}` };
  };

  const selectedBadge = badgeFor(selectedEnv);

  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="flex bg-zinc-800 p-1 rounded-xl border border-white/5 shadow-inner">
        {(['DEV', 'QAS', 'PRD'] as const).map((env) => {
          const badge = badgeFor(env);
          return (
            <button
              key={env}
              type="button"
              onClick={() => onChange(env)}
              title={badge.title}
              className={`relative px-4 py-1.5 rounded-lg text-xs font-bold tracking-wide transition-all ${
                selectedEnv === env ? ACTIVE_CLASS[env] : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {env}
              {badge.color !== 'none' && (
                <span
                  className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border border-zinc-900 ${
                    badge.color === 'green' ? 'bg-emerald-500' : 'bg-orange-400'
                  }`}
                />
              )}
            </button>
          );
        })}
      </div>
      {selectedEnv !== 'DEV' && (
        <span className={`text-[10px] font-medium ${selectedBadge.color === 'green' ? 'text-emerald-400' : 'text-orange-400'}`}>
          {selectedBadge.color === 'green' ? '🟢' : '🟠'} {selectedBadge.title}
        </span>
      )}
    </div>
  );
}
